/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import {
  toast,
  Id,
  ToastContainer as ToastifyContainer,
  CloseButtonProps,
  Slide,
  ToastContent,
  ToastOptions,
} from 'react-toastify';

import TestIds from '../testIds';

import './useToast.scss';

export const ToastContainer = (): JSX.Element => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down('md'));

  const closeButton = ({ closeToast }: CloseButtonProps): JSX.Element => (
    <Box className='fl-use-toast__toast__close'>
      <Button
        variant='farmlands2.0_material-icon'
        color='secondary'
        onClick={closeToast}
        data-testid={TestIds.components.toastify.closeBtn}
      >
        close
      </Button>
    </Box>
  );

  return (
    <ToastifyContainer
      transition={Slide}
      limit={1}
      position={isMd ? 'top-center' : 'bottom-center'}
      autoClose={2000}
      newestOnTop
      hideProgressBar
      closeOnClick
      closeButton={closeButton}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme='dark'
      className='fl-use-toast__toast-container'
    />
  );
};

export const useToast = () => {
  const toastId = React.useRef<Id | undefined>(undefined);

  const notify = (content: ToastContent<unknown>, options?: ToastOptions): void => {
    dismissAll();
    toastId.current = toast(content, {
      className: 'fl-use-toast__toast',
      ...options,
    });
  };

  const dismiss = () => toast.dismiss(toastId.current);

  const dismissAll = () => toast.dismiss();

  return {
    notify,
    dismiss,
    dismissAll,
  };
};

export default useToast;
